import { useState, useEffect } from "react"
import Button from "@/components/Button"
import ModePanel from "@/components/interface/ModePanel"
import WarningCallout from "@/components/WarningCallout"
import { DEVICE_CONNECTED, DEVICE_DISCONNECTED, NO_DEVICES, DEVICE_UNSUPPORTED } from "@/constants"
import { useAuth } from "@/contexts/auth"
import { useSerialDevice } from "@/contexts/serial-device"
import { ReactComponent as BrowserWarningIcon } from "@/icons/browser-warning.svg"
import { ReactComponent as ElementIcon } from "@/icons/element-icon.svg"
import { ReactComponent as ToolsIcon } from "@/icons/tools-icon.svg"
import { ReactComponent as PlusIcon } from "@/icons/plus-icon-simple.svg"
import EqualiserSidebarFooter from "@/components/interface/EqualiserSidebarFooter"
import cx from "classnames"
import { toast } from "react-toastify"
import { axios } from "@/contexts/auth"

// Define prop types explicitly
type EqualiserSidebarProps = {
  mode: string
  presets: any[] // Replace 'any' with specific Preset type if available
  activePreset: any // Replace 'any' with specific Preset type if available
  onModeChanged: (mode: string) => void
  onPresetSelected: (preset: any) => void // Replace 'any'
  onDeletePresetClicked: (preset: any) => void // Replace 'any'
  onRenamePresetClicked: (preset: any) => void // Replace 'any'
  onExportAllClicked: () => void
  onImportPresetClicked: () => void
  deviceSettings: any // Replace 'any' with specific type
  deviceProfiles: any[] // Replace 'any' with specific type
  activeDeviceProfile: any // Replace 'any' with specific type
  onSaveToDevice: (name: string) => void
  onDeleteDeviceProfile: (id: number) => void
  onActivateDeviceProfile: (id: number) => void
  deviceSupportsProfiles: boolean
  activePresetOutdated: boolean
  isPresetLoading: boolean
  onSavePresetClicked: (preset: any) => void // Ensure this is included and typed correctly (replace 'any')
  createPreset: (preset: { name: string; mode: string; config: object }) => Promise<any>
}

const EqualiserSidebar: React.FC<EqualiserSidebarProps> = ({
  mode,
  presets,
  activePreset,
  onModeChanged,
  onPresetSelected,
  onDeletePresetClicked,
  onRenamePresetClicked,
  onExportAllClicked,
  onImportPresetClicked,
  deviceSettings,
  deviceProfiles,
  activeDeviceProfile,
  onSaveToDevice,
  onDeleteDeviceProfile,
  onActivateDeviceProfile,
  deviceSupportsProfiles,
  activePresetOutdated,
  isPresetLoading,
  onSavePresetClicked,
  createPreset,
}) => {
  const { user } = useAuth()
  const { status } = useSerialDevice()

  return (
    <>
      <aside className="relative z-40 flex h-full w-full flex-col justify-around border-l border-mine-shaft bg-dark px-4">
        <div className="flex flex-1 flex-col justify-start overflow-y-auto py-8 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-jaguar hover:[&::-webkit-scrollbar-thumb]:bg-silver [&::-webkit-scrollbar-track]:bg-dark-jaguar/40 [&::-webkit-scrollbar]:w-2">
          {status === DEVICE_UNSUPPORTED && <UnsupportedWarning />}
          {[NO_DEVICES, DEVICE_DISCONNECTED].includes(status) && <NoDevicesWarning />}
          {status === DEVICE_CONNECTED && (
            <ModePanel
              mode={mode}
              presets={presets}
              activePreset={activePreset}
              onModeChanged={onModeChanged}
              onPresetSelected={onPresetSelected}
              onDeletePresetClicked={onDeletePresetClicked}
              onRenamePresetClicked={onRenamePresetClicked}
              onSearchTextChanged={() => {}}
              deviceSettings={deviceSettings}
              deviceProfiles={deviceProfiles}
              activeDeviceProfile={activeDeviceProfile}
              onSaveToDevice={onSaveToDevice}
              onDeleteDeviceProfile={onDeleteDeviceProfile}
              onActivateDeviceProfile={onActivateDeviceProfile}
              deviceSupportsProfiles={deviceSupportsProfiles}
              activePresetOutdated={activePresetOutdated}
              isPresetLoading={isPresetLoading}
              onSavePresetClicked={onSavePresetClicked}
              createPreset={createPreset}
            />
          )}
        </div>
        <div className="pb-5">
          <EqualiserSidebarFooter />
        </div>
      </aside>
    </>
  )
}

const UnsupportedWarning = () => {
  return (
    <WarningCallout
      icon={<BrowserWarningIcon className="h-12" />}
      heading="Browser Not Supported"
      content={
        <>
          <p>
            Sorry, your current browser is not supported by JDS Labs Core. Supported browsers
            include:
          </p>
          <ul className="list-disc pl-5">
            <li>Google Chrome</li>
            <li>Microsoft Edge</li>
            <li>Opera</li>
            <li>Brave</li>
            <li>Firefox*</li>
          </ul>
          <p>
            *Firefox requires the{" "}
            <a
              href="https://addons.mozilla.org/en-US/firefox/addon/webserial-for-firefox/"
              className="text-red"
              target="_blank"
            >
              WebSerial for Firefox
            </a>{" "}
            extension.
          </p>
          <p>Safari and Chrome for Android do not support WebSerial.</p>
        </>
      }
    />
  )
}

const NoDevicesWarning = () => {
  const { request } = useSerialDevice()
  return (
    <WarningCallout
      heading={
        <>
          <ElementIcon className="h-4 w-auto text-white" />
          <h2>Connect Element IV</h2>
        </>
      }
      content={
        <>
          <p>
            To connect Element IV, first make sure the device is plugged in to your computer via USB
            and powered on.
          </p>
          <p>
            Once complete, you are ready to connect. You will be prompted by your browser to
            establish a connection with Element IV.
          </p>
          <p>
            <Button
              type="secondary"
              size="xs"
              outline
              onClick={() => {
                request()
              }}
            >
              Connect to Device
            </Button>
          </p>
        </>
      }
    />
  )
}

export default EqualiserSidebar
